import React, { useEffect, useState } from "react";
import { useNotification } from "../../../hooks/useNotification";
import { useLocation, useNavigate } from "react-router-dom";
import { useDataAuth } from "../../../hooks/useDataAuth";
import { useTranslation } from "react-i18next";

import FormControlLabel from "@mui/material/FormControlLabel";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import LoadingOverlay from "../../../components/others/LoadingOverlay";
import { ActionButtons } from "../../../components/others/ActionButtons";
import {
    fetchActionsSelectById,
    updateActionSelection,
} from "../../../services/actionSelection.service";
import { Dialog } from "../../../components/others/Dialog";

const buildInitialRowData = (fields = []) =>
    fields.reduce((acc, field) => {
        acc[field.id] = "";
        return acc;
    }, {});


const EMPTY_NEW_FIELD = {
    id: "",
    label: "",
    type: "text",
    required: true
};

export const LayoutActionSelection = ({
    embedded = false,
    config: configProp = null,
    value = null,
    onChange,
    onClose,
    multiple,
    allowSchemaEditing
}) => {
    const [ sendNotification ] = useNotification();
    const [ { userSession } ] = useDataAuth();
    const location = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [ configuration, setConfiguration ] = useState(configProp || { table: [] });
    const [ rows, setRows ] = useState([]);
    const [ rowData, setRowData ] = useState({});
    const [ editingIndex, setEditingIndex ] = useState(null);
    const [ errorInputs, setErrorInputs ] = useState({});
    const [ fieldInfo, setFieldInfo ] = useState(null);
    const [ loading, setLoading ] = useState(false);
    const [ isView, setIsView ] = useState(false);
    const [ showNewField, setShowNewField ] = useState(false);
    const [ newField, setNewField ] = useState(EMPTY_NEW_FIELD);
    const isMultiple = multiple ?? !embedded;
    const canEditSchema = allowSchemaEditing ?? !embedded;
    const fields = configuration.form || configuration.table || [];
    const handleClose = () => onClose ? onClose() : navigate(-1);

    useEffect(() => {
        const fetchData = async (id) => {
            try {
                setLoading(true);
                const response = await fetchActionsSelectById(id, userSession.token);
                if (response?.data?.[0]) {
                    const dto = response.data[0];
                    setFieldInfo(dto);
                    const parsedConfiguration = dto.configuration ? JSON.parse(dto.configuration) : { table: [] };
                    setConfiguration(parsedConfiguration);
                    setRowData(buildInitialRowData(parsedConfiguration.table));
                    const parsedValue = JSON.parse(dto.value || "[]");
                    setRows(Array.isArray(parsedValue) ? parsedValue : []);
                }
            } catch (error) {
                console.error(error);
                sendNotification({ msg: t("messages.eventError"), variant: "error" });
            } finally {
                setLoading(false);
            }
        };

        if (embedded) {
            let parsedConfiguration = configProp || { form: [] };
            if (typeof configProp === "string") {
                try {
                    parsedConfiguration = JSON.parse(configProp);
                } catch (error) {
                    console.error("Invalid action selection configuration", error);
                    parsedConfiguration = { form: [] };
                }
            }
            setConfiguration(parsedConfiguration);
            if (isMultiple) {
                const initialRows = Array.isArray(value) ? value : [];
                setRows(initialRows);
                setRowData(buildInitialRowData(parsedConfiguration.form || parsedConfiguration.table));
            } else {
                setRowData({
                    ...buildInitialRowData(parsedConfiguration.form || parsedConfiguration.table),
                    ...(value && !Array.isArray(value) ? value : {})
                });
            }
            return;
        }

        if (location.state?.config) {
            const { config } = location.state;
            if (config.data?.work_flow === "FLOW_PROCESS-VIEW_CONTACT") setIsView(true);
            if (config.data?.id) fetchData(config.data.id);
        }
    }, [configProp, embedded, isMultiple, location.state, userSession.token, value]);

    const getFieldError = (field, value) => {
        if (field.required && (!value || String(value).trim() === "")) {
            return "Campo requerido";
        }
        return "";
    };

    const validateField = (field, value) => {
        const msg = getFieldError(field, value);
        setErrorInputs((prev) => ({ ...prev, [field.id]: { isError: !!msg, messageError: msg }}));
        return !msg;
    };


    const validateAllFields = () => {
        let valid = true;
        fields.forEach((field) => {
            if (!validateField(field, rowData[field.id])) {
                valid = false;
            }
        });
        return valid;
    };

    const handleRowChange = (e) => {
        const { name, value } = e.target;
        setRowData((prev) => ({ ...prev, [name]: value }));
        const field = fields.find((f) => f.id === name);
        if (field) validateField(field, value);
    };


    const handleAddRow = () => {
        if (!validateAllFields()) return;
        if (editingIndex !== null) {
            setRows((prev) => prev.map((r, i) => (i === editingIndex ? { ...rowData } : r)));
            setEditingIndex(null);
        } else {
            setRows((prev) => [...prev, {...rowData}]);
        }
        setRowData(buildInitialRowData(fields));
        setErrorInputs({});
    };

    const handleEditRow = (index) => {
        setEditingIndex(index);
        setRowData(rows[index]);
        setErrorInputs({});
    };

    const handleDeleteRow = (index) => setRows(rows.filter((_, i) => i !== index));

    const handleNewFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewField((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleConfirmNewField = () => {
        const fieldToAdd = {
            ...newField,
            id: newField.id.trim(),
            label: newField.label.trim(),
        };
        console.log("fieldToAdd ", fieldToAdd)
        if (!fieldToAdd.id) return;
        if (fields.some((f) => f.id === fieldToAdd.id)) {
            sendNotification({ msg: "El campo ya existe", variant: "warning" });
            return;
        }
        const schemaKey = configuration.form ? "form" : "table";
        setConfiguration((prev) => ({ ...prev, [schemaKey]: [ ...(prev[schemaKey] || []), fieldToAdd ] }));
        setRowData((prev) => ({ ...prev, [ fieldToAdd.id ]: "" }));
        setNewField(EMPTY_NEW_FIELD);
        setShowNewField(false);
    };

    const handleSave = async () => {
        if (embedded) {
            if (!isMultiple) {
                if (!validateAllFields()) return;
                onChange?.({ ...rowData });
            } else {
                let nextRows = rows;
                const hasPendingRow = fields.some((field) => {
                    const fieldValue = rowData[field.id];
                    return fieldValue !== null && fieldValue !== undefined && String(fieldValue).trim() !== "";
                });
                if (hasPendingRow) {
                    if (!validateAllFields()) return;
                    nextRows = editingIndex !== null
                        ? rows.map((row, index) => index === editingIndex ? { ...rowData } : row)
                        : [...rows, { ...rowData }];
                }
                onChange?.(nextRows);
            }
            handleClose();
            return;
        }

        try {
            const newValue = JSON.stringify(rows);
            const newConfiguration = JSON.stringify(configuration);
            await updateActionSelection(
                fieldInfo.id_company_action_selection,
                fieldInfo?.id_company,
                newValue,
                newConfiguration,
                userSession.token
            );
            sendNotification({ msg: t("messages.eventSucces"), variant: "success" });
            handleClose();
        } catch (e) {
            console.error(e);
            sendNotification({ msg: t("messages.eventError"), variant: "error" });
        }
    };

    return (
        <>
            <Box sx={ { mx: "auto" } }>
                <Card variant={embedded ? "outlined" : "elevation"} sx={ { mb: embedded ? 0 : 3 } }>
                    <CardContent>
                        {!embedded && (
                            <Typography variant="h6" sx={ { mb: 2 } }>
                                { isView ? t("view") : t("edit") }
                            </Typography>
                        )}

                        <Grid container spacing={ 2 }>
                            {fields.map((field) => (
                                <Grid key={field.id} size={{ xs: 4, sm: 4 }}>
                                    <TextField
                                        fullWidth
                                        label={t(field.label)}
                                        name={field.id}
                                        value={rowData[field.id] ?? ""}
                                        type={field.type ?? "text"}
                                        size="small"
                                        onChange={handleRowChange}
                                        disabled={isView}
                                        required={field.required}
                                        error={!!errorInputs[field.id]?.isError}
                                        helperText={errorInputs[field.id]?.messageError || field.help}
                                    />
                                </Grid>
                            ))}

                            {isMultiple && (
                                <Grid size={{ xs: 12, sm: 2 }}>
                                    <IconButton color="primary" onClick={handleAddRow} disabled={isView}>
                                        {editingIndex !== null ? <SaveIcon /> : <AddIcon />}
                                    </IconButton>
                                </Grid>
                            )}
                            {canEditSchema && !isView && (
                                <Grid size={{ xs: 12, sm: 2 }}>
                                    <Tooltip title={t("add")}>
                                        <IconButton color="secondary" onClick={() => setShowNewField(true)}>
                                            <PlaylistAddIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}
                        </Grid>

                        {isMultiple && (
                            <Box mt={ 4 }>
                                <Typography variant="h6">{ t("values") }</Typography>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            {fields.map((field) => <TableCell key={field.id}>{t(field.label)}</TableCell>)}
                                            <TableCell align="right">
                                                { t("common.actions") }
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row, index) => (
                                            <TableRow key={index}>
                                                {fields.map((field) => <TableCell key={field.id}>{row[field.id]}</TableCell>)}
                                                <TableCell align="right">
                                                    <IconButton onClick={() => handleEditRow(index)} disabled={isView}>
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDeleteRow(index)} disabled={isView}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        <Box sx={ { textAlign: "right", mt: 3 } }>
                            <ActionButtons
                                onCancel={handleClose}
                                onSubmit={handleSave}
                                submitLabel={t("save")}
                                disabledSubmit={loading || isView}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <LoadingOverlay isOpen={loading} />
            <Dialog
                openDialog={showNewField}
                widthDialog="md"
                title={t("add")}
                styleContent={{ pt: 8 }}
                content={
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <TextField fullWidth label="id" name="id"
                                    value={newField.id}
                                    onChange={handleNewFieldChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <TextField fullWidth label="label" name="label"
                                    value={newField.label}
                                    onChange={handleNewFieldChange}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <FormControlLabel
                                    control={ <Checkbox checked={newField.required} name="required" onChange={handleNewFieldChange}/> }
                                    label="Required"
                                />
                            </Grid>

                        </Grid>
                    </Box>
                }
                actions={
                    <ActionButtons
                        onCancel={() => setShowNewField(false)}
                        onSubmit={handleConfirmNewField}
                        submitLabel={t("save")}
                    />
                }
                onClose={() => setShowNewField(false)}
            />

        </>
    );
};
